"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import Image from "next/image";
import { usePathname } from "next/navigation";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname, type Region } from "@/lib/region";
import { resolveRichTextHref } from "@/lib/storyblok-link";
import { useRovingFocus } from "@/lib/use-roving-focus";

type Asset = { filename?: string; alt?: string };

// `description` accepts both shapes: legacy plain string or migrated richtext.
type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string };
};
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

type StepByStepStep = SbBlokData & {
  title?: string;
  description?: string | RichTextDoc;
  image?: Asset;
};

type StepByStepBlok = SbBlokData & {
  title?: string;
  steps?: StepByStepStep[];
  bg_color?: string;
};

function richTextHasContent(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(richTextHasContent);
}

// The description toolbar only allows bold / italic / link.
function renderStepNode(
  node: RichTextNode,
  key: number,
  region?: Region,
): ReactNode {
  switch (node.type) {
    case "paragraph":
      return (
        <p key={key}>
          {node.content?.map((n, i) => renderStepNode(n, i, region))}
        </p>
      );
    case "hard_break":
      return <br key={key} />;
    case "text": {
      let el: ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className="underline underline-offset-2 hover:opacity-80"
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderStepNode(n, i, region)) ?? null;
  }
}

function StepDescription({
  value,
  isOpen,
  region,
}: {
  value: string | RichTextDoc;
  isOpen: boolean;
  region: Region;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [scrollable, setScrollable] = useState(false);

  // Reset the scroll back to the top when the card closes, so reopening it
  // doesn't show the text where it was last left off.
  useEffect(() => {
    if (!isOpen && scrollRef.current) scrollRef.current.scrollTop = 0;
  }, [isOpen]);

  let inner: ReactNode = null;
  if (typeof value === "string") {
    inner = <p className="whitespace-pre-line">{value}</p>;
  } else if (richTextHasContent(value)) {
    inner = (
      <div className="flex flex-col gap-3">
        {value.content?.map((n, i) => renderStepNode(n, i, region))}
      </div>
    );
  }

  // Only a tab stop when the text genuinely overflows — measure the text
  // itself, not the padded box, so the fade reserve doesn't trip Chrome's
  // focusable-scroller ring when everything already fits.
  useEffect(() => {
    const el = scrollRef.current;
    const content = contentRef.current;
    if (!el || !content) return;
    const check = () =>
      setScrollable(content.offsetHeight > el.clientHeight + 4);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    ro.observe(content);
    return () => ro.disconnect();
  }, []);

  if (!inner) return null;
  return (
    <div className="relative min-h-0 flex-1">
      <div
        ref={scrollRef}
        data-step-scroll
        tabIndex={scrollable ? 0 : -1}
        role={scrollable ? "group" : undefined}
        aria-label={scrollable ? "Step description, scroll for more" : undefined}
        className={`scrollbar-none h-full touch-pan-y overflow-y-auto overscroll-contain pb-14 text-body leading-[1.56] outline-none [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden ${
          scrollable
            ? "focus-visible:rounded-[8px] focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-purple-01"
            : ""
        }`}
      >
        <div ref={contentRef}>{inner}</div>
      </div>
      {scrollable && (
        <div
          aria-hidden
          className="from-purple-05 pointer-events-none absolute inset-x-0 bottom-0 h-10 bg-linear-to-t to-transparent"
        />
      )}
    </div>
  );
}

export default function StepByStep({ blok }: { blok: StepByStepBlok }) {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isScrollable, setIsScrollable] = useState(false);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);
  const steps = blok.steps || [];
  const { bg, hex } = resolveBg(blok.bg_color, "dusk-01");
  const region = regionFromPathname(usePathname() ?? "/");

  const scrollerRef = useRef<HTMLDivElement>(null);
  const cardRefs = useRef<(HTMLLIElement | null)[]>([]);
  const triggerRefs = useRef<(HTMLButtonElement | null)[]>([]);

  useEffect(() => {
    if (openIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      const i = openIndex;
      setOpenIndex(null);
      requestAnimationFrame(() => triggerRefs.current[i]?.focus());
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [openIndex]);


  const revealCard = (index: number) => {
    const scroller = scrollerRef.current;
    const card = cardRefs.current[index];
    if (!scroller || !card) return;
    const sRect = scroller.getBoundingClientRect();
    const cRect = card.getBoundingClientRect();
    const cardLeft = cRect.left - sRect.left + scroller.scrollLeft;
    const target = cardLeft + cRect.width / 2 - scroller.clientWidth / 2;
    const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
    scroller.scrollTo({
      left: Math.max(0, Math.min(target, max)),
      behavior: "smooth",
    });
  };

  const handleTriggerKey = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    index: number,
  ) => {
    const total = triggerRefs.current.length;
    if (total === 0) return;
    let next = index;
    if (e.key === "ArrowRight") next = (index + 1) % total;
    else if (e.key === "ArrowLeft") next = (index - 1 + total) % total;
    else if (e.key === "Home") next = 0;
    else if (e.key === "End") next = total - 1;
    else return;
    e.preventDefault();
    setActiveIndex(next);
    // preventScroll so the browser's focus nudge doesn't fight the centring
    // and slam the card flush to the edge, hiding the next one.
    triggerRefs.current[next]?.focus({ preventScroll: true });
    revealCard(next);
  };

  // Mouse navigation — scroll roughly a viewport per click.
  const scrollByPage = (dir: number) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: "smooth" });
  };

  // One tab stop for the arrow pair; Left/Right move between them, Tab leaves.
  const { ref: arrowsRef, rovingProps: arrowsRovingProps } =
    useRovingFocus<HTMLDivElement>();

  const arrowClass =
    "flex size-11 items-center justify-center rounded-full border border-black/10 text-purple-05 transition-colors hover:border-purple-03 hover:text-purple-03 aria-disabled:pointer-events-none aria-disabled:opacity-30";

  // The card's 700ms width transition grows scrollWidth; clamp against live max.
  useEffect(() => {
    if (openIndex === null) return;
    const card = cardRefs.current[openIndex];
    const scroller = scrollerRef.current;
    if (!card || !scroller) return;

    const isLg = window.matchMedia("(min-width: 1024px)").matches;
    const expandedWidth = isLg ? 588 : window.innerWidth - 40;

    const cardRect = card.getBoundingClientRect();
    const scrollerRect = scroller.getBoundingClientRect();
    const cardLeftInScroller =
      cardRect.left - scrollerRect.left + scroller.scrollLeft;
    const finalTarget =
      cardLeftInScroller + expandedWidth / 2 - scroller.clientWidth / 2;

    const startScroll = scroller.scrollLeft;
    const duration = 700;
    let startTs: number | null = null;
    let rafId = 0;

    const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);

    const tick = (ts: number) => {
      if (startTs === null) startTs = ts;
      const progress = Math.min((ts - startTs) / duration, 1);
      const desired = startScroll + (finalTarget - startScroll) * easeOut(progress);
      const max = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
      scroller.scrollLeft = Math.max(0, Math.min(desired, max));
      if (progress < 1) rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [openIndex]);

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const update = () => {
      const rect = scroller.getBoundingClientRect();
      const center = rect.left + rect.width / 2;
      let closest = 0;
      let minDist = Infinity;
      cardRefs.current.forEach((card, i) => {
        if (!card) return;
        const cRect = card.getBoundingClientRect();
        const cCenter = cRect.left + cRect.width / 2;
        const dist = Math.abs(cCenter - center);
        if (dist < minDist) {
          minDist = dist;
          closest = i;
        }
      });
      const maxScroll = scroller.scrollWidth - scroller.clientWidth;
      if (scroller.scrollLeft <= 1) closest = 0;
      else if (scroller.scrollLeft >= maxScroll - 1) closest = steps.length - 1;
      setActiveIndex(closest);
      setAtStart(scroller.scrollLeft <= 1);
      setAtEnd(scroller.scrollLeft >= maxScroll - 1);
      // Scroller padding inflates `scrollWidth`; measure the card span.
      const first = cardRefs.current[0];
      const last = cardRefs.current[steps.length - 1];
      if (first && last) {
        const span = last.offsetLeft + last.offsetWidth - first.offsetLeft;
        setIsScrollable(span > scroller.clientWidth - 16);
      } else {
        setIsScrollable(false);
      }
    };

    update();
    scroller.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    return () => {
      scroller.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
    };
  }, [steps.length]);

  // Redirect vertical wheel events to the page scroll, otherwise the
  // overflow-x container captures them and the page gets stuck.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;

    const onWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return;
      // If the pointer is over an open card's scrollable description, let it
      // scroll its own text (overscroll-contain keeps it from chaining out).
      const inner = (e.target as HTMLElement)?.closest?.(
        "[data-step-scroll]",
      ) as HTMLElement | null;
      if (inner && inner.scrollHeight > inner.clientHeight) return;
      e.preventDefault();
      window.scrollBy({ top: e.deltaY });
    };

    scroller.addEventListener("wheel", onWheel, { passive: false });
    return () => scroller.removeEventListener("wheel", onWheel);
  }, []);

  if (steps.length === 0) return null;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="pt-[60px] pb-[80px] lg:pt-[93px] lg:pb-[120px]">

        <div className="mx-auto flex w-full max-w-[1280px] items-center gap-6 px-[clamp(1.25rem,4vw,2.5rem)]">
          {blok.title && (
            <h2
              className={`flex-1 text-center text-[clamp(2.25rem,4vw,3rem)] font-bold leading-[1.08] text-purple-05 ${
                isScrollable ? "lg:text-left" : ""
              }`}
            >
              {blok.title}
            </h2>
          )}
          {isScrollable && (
            <div
              ref={arrowsRef}
              {...arrowsRovingProps}
              className="hidden shrink-0 items-center gap-2 lg:flex"
            >
              <button
                type="button"
                data-roving
                onClick={() => scrollByPage(-1)}
                aria-disabled={atStart || undefined}
                aria-label="Previous steps"
                className={arrowClass}
              >
                <ChevronLeft className="size-5" strokeWidth={2} />
              </button>
              <button
                type="button"
                data-roving
                onClick={() => scrollByPage(1)}
                aria-disabled={atEnd || undefined}
                aria-label="Next steps"
                className={arrowClass}
              >
                <ChevronRight className="size-5" strokeWidth={2} />
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 lg:mt-10">
          <div className="relative lg:mx-auto lg:max-w-[1280px] lg:px-[clamp(1.25rem,4vw,2.5rem)]">
          <div
            ref={scrollerRef}
            tabIndex={-1}
            className={`touch-pan-x touch-pan-y overflow-y-clip scrollbar-none [&::-webkit-scrollbar]:hidden py-1 focus:outline-none ${
              isScrollable ? "overflow-x-auto" : "overflow-x-clip"
            }`}
          >
            <ul className="mx-auto flex w-max items-start gap-[25px] px-[clamp(1.25rem,4vw,2.5rem)] pt-[42px] lg:gap-7 lg:px-0">
              {steps.map((step, i) => {
                const isOpen = openIndex === i;
                const stepNumber = String(i + 1).padStart(2, "0");

                return (
                  <li
                    key={step._uid as string}
                    ref={(el) => {
                      cardRefs.current[i] = el;
                    }}
                    {...storyblokEditable(step)}
                    className={`relative shrink-0 perspective-[1500px] transition-[width] duration-700 ease-out ${
                      isOpen
                        ? "w-[calc(100vw-2.5rem)] lg:w-[588px]"
                        : "w-[280px] lg:w-[278px]"
                    }`}
                  >
                    {/* Number badge: solid forest-03 by default, swaps
                        to white-on-forest-03 while the card is flipped
                        open so it stays legible against the dark back. */}
                    <div
                      className={`pointer-events-none absolute top-[-37px] left-1/2 z-10 flex size-[74px] -translate-x-1/2 items-center justify-center rounded-full transition-colors duration-300 lg:top-[-42px] lg:size-[83px] ${
                        isOpen ? "bg-white" : "bg-forest-03"
                      }`}
                    >
                      <span
                        className={`text-[28px] font-semibold leading-none tracking-[0.02em] transition-colors duration-300 lg:text-h4 ${
                          isOpen ? "text-forest-03" : "text-white"
                        }`}
                      >
                        {stepNumber}
                      </span>
                    </div>

                    <div
                      className={`relative h-[420px] transition-transform duration-700 ease-out transform-3d lg:h-[482px] ${
                        isOpen ? "transform-[rotateY(180deg)]" : ""
                      }`}
                    >
                      <button
                        ref={(el) => {
                          triggerRefs.current[i] = el;
                        }}
                        type="button"
                        onClick={() => setOpenIndex(i)}
                        onKeyDown={(e) => handleTriggerKey(e, i)}
                        aria-expanded={isOpen}
                        aria-hidden={isOpen}
                        tabIndex={!isOpen && i === 0 ? 0 : -1}
                        className="video-poster-btn absolute inset-y-0 left-0 flex w-[280px] cursor-pointer flex-col overflow-hidden rounded-[18px] text-left backface-hidden lg:w-[278px] lg:rounded-[20px]"
                      >
                        <div className="relative aspect-2560/1803 w-full shrink-0 bg-purple-01">
                          {step.image?.filename && (
                            <Image
                              src={step.image.filename}
                              alt={step.image.alt || ""}
                              fill
                              sizes="280px"
                              className="object-cover"
                            />
                          )}
                        </div>

                        <div className="flex flex-1 flex-col bg-white px-7 pt-9 pb-6 lg:px-8 lg:pt-10 lg:pb-7">
                          {step.title && (
                            <p className="text-[28px] font-bold leading-[1.054] text-black lg:text-h4 lg:tracking-[0.02em]">
                              {step.title}
                            </p>
                          )}
                          <span className="mt-auto pt-4 text-body font-bold leading-[1.054] text-purple-03">
                            Learn more
                          </span>
                        </div>
                      </button>

                      <div
                        inert={!isOpen}
                        className="absolute inset-0 flex flex-col gap-4 rounded-[18px] bg-purple-05 px-7 pt-16 pb-9 text-white backface-hidden transform-[rotateY(180deg)] lg:rounded-[20px] lg:px-11 lg:pt-16 lg:pb-11"
                      >
                        {step.title && (
                          <p className="text-body font-bold leading-[1.12]">
                            {step.title}
                          </p>
                        )}
                        {step.description && (
                          <StepDescription
                            value={step.description}
                            isOpen={isOpen}
                            region={region}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => {
                            setOpenIndex(null);
                            requestAnimationFrame(() =>
                              triggerRefs.current[i]?.focus(),
                            );
                          }}
                          tabIndex={isOpen ? 0 : -1}
                          className="cursor-pointer self-start text-h6 font-bold leading-[1.054] text-purple-01 hover:underline"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          </div>

            {/* Edge fades on desktop keep the carousel a fixed-width column. */}
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-y-0 left-0 z-20 hidden w-16 transition-opacity duration-200 lg:left-[clamp(1.25rem,4vw,2.5rem)] lg:block ${
                isScrollable && !atStart ? "opacity-100" : "opacity-0"
              }`}
              style={{ backgroundImage: `linear-gradient(to right, ${hex}, transparent)` }}
            />
            <div
              aria-hidden
              className={`pointer-events-none absolute inset-y-0 right-0 z-20 hidden w-16 transition-opacity duration-200 lg:right-[clamp(1.25rem,4vw,2.5rem)] lg:block ${
                isScrollable && !atEnd ? "opacity-100" : "opacity-0"
              }`}
              style={{ backgroundImage: `linear-gradient(to left, ${hex}, transparent)` }}
            />
          </div>

          {isScrollable && (
            <div className="mt-6 flex justify-center lg:hidden">
              <div className="flex items-center">
                {steps.map((_, i) => (
                  <button
                    key={i}
                    type="button"
                    tabIndex={-1}
                    onClick={() => {
                      cardRefs.current[i]?.scrollIntoView({
                        behavior: "smooth",
                        inline: "center",
                        block: "nearest",
                      });
                    }}
                    aria-label={`Go to step ${i + 1}`}
                    className="flex h-6 min-w-6 cursor-pointer items-center justify-center"
                  >
                    <span
                      aria-hidden
                      className={`block h-1.5 rounded-full transition-all duration-300 ${
                        activeIndex === i
                          ? "w-8 bg-purple-03"
                          : "w-2 bg-purple-01"
                      }`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
